import { Button } from '@cocoonspace/ui/components/button'
import { ButtonGroup } from '@cocoonspace/ui/components/button-group'
import { useLingui } from '@lingui/react/macro'
import { endOfWeek, format, startOfWeek } from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { fr } from 'date-fns/locale/fr'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { MonthSelect } from './month-select'
import { useSchedulerCalendar } from './provider'
import { ViewPicker } from './view-picker'
import { YearSelect } from './year-select'
import type { SchedulerVisiblePeriod } from './types'

const ActivePeriod = ({
	date,
	locale,
	view,
	weekVisiblePeriod,
}: {
	date: Date
	locale: 'en' | 'fr'
	view: 'month' | 'week' | 'day' | 'agenda'
	weekVisiblePeriod: SchedulerVisiblePeriod | null
}) => {
	const formatLocale = locale === 'en' ? enUS : fr

	if (view === 'month') {
		return (
			<div className='text-sm font-bold capitalize'>
				{format(date, 'MMMM yyyy', { locale: formatLocale })}
			</div>
		)
	}

	if (view === 'week' && weekVisiblePeriod) {
		return (
			<div className='text-sm font-bold capitalize'>
				{format(weekVisiblePeriod.start, 'd MMM', { locale: formatLocale })} -{' '}
				{format(weekVisiblePeriod.end, 'd MMM yyyy', {
					locale: formatLocale,
				})}
			</div>
		)
	}

	const weekStartsOn = locale === 'en' ? 0 : 1
	const start = startOfWeek(date, { weekStartsOn })
	const end = endOfWeek(date, { weekStartsOn })

	return (
		<div className='text-sm font-bold capitalize'>
			{format(start, 'd MMM', { locale: formatLocale })} -{' '}
			{format(end, 'd MMM yyyy', { locale: formatLocale })}
		</div>
	)
}

export const SchedulerCalendarHeader = () => {
	const { t } = useLingui()

	const {
		currentDate,
		goNext,
		goPrev,
		locale,
		setCurrentDate,
		view,
		visiblePeriod,
	} = useSchedulerCalendar()

	return (
		<div className='flex flex-col items-center justify-between gap-4 border-b p-4 md:flex-row'>
			<div className='flex w-full flex-wrap items-center gap-4 max-md:justify-between md:flex-1'>
				<ActivePeriod
					date={currentDate}
					locale={locale}
					view={view}
					weekVisiblePeriod={visiblePeriod}
				/>

				<ButtonGroup>
					<Button
						icon
						onClick={goPrev}
						size='sm'
						type='button'
						variant='outline'
						className='border-border!'
					>
						<ChevronLeftIcon />
					</Button>

					<Button
						icon
						onClick={goNext}
						size='sm'
						type='button'
						variant='outline'
						className='border-border!'
					>
						<ChevronRightIcon />
					</Button>

					<Button
						onClick={() => setCurrentDate(new Date())}
						size='sm'
						type='button'
						variant='outline'
						className='border-border!'
					>
						{t`Aujourd’hui`}
					</Button>
				</ButtonGroup>
			</div>

			<div className='flex items-center gap-4 max-md:w-full max-md:justify-between md:flex-1 md:justify-end'>
				{/*<SpaceCalendarAddBtn />*/}

				<div className='flex items-center'>
					<MonthSelect />
					<YearSelect />
				</div>

				<ViewPicker />
			</div>
		</div>
	)
}
